import { createSlice } from '@reduxjs/toolkit';
import { ReduxStatus } from '../../../../shared/types';
import {
  getCommunityMembers,
  updateCommunityMember,
  createCommunityAdmin,
  getBasicCommunityMembers,
  getRecentMembers,
  updateUserRole,
  createAdministrator,
  deleteMembers,
  verifyUser,
  activateMembers,
  deactivateMembers,
  updateUserVerification,
  getCommunityGuests,
} from './opeartors';
import { MemberModel } from '../models/Member.model';
import { GroupModel } from '../models/Groups.model';
import { BasicMemberModel } from '../models/BasicMember.model';
import { PaginationMetaModel } from '../../../../shared/components/pagination/model/Pagination.model';
import { BasicGroupsModel } from '../models/BasicGroups.model';
import { RecentMemberModel } from '../models/RecentMember.model';
import { resetAll } from '../../../../shared/infra/redux/baseActions';
import { GuestModel } from '../models/Guest.model';

export interface MembersState {
  members: MemberModel[];
  memberPagination: PaginationMetaModel;
  groups: GroupModel[];
  groupsPagination: PaginationMetaModel;
  guests: GuestModel[];
  guestPagination: PaginationMetaModel;
  basicMembers: BasicMemberModel[];
  basicGroups?: BasicGroupsModel[];
  recentMembers: RecentMemberModel[];
  createCommunityGroupStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  createCommunityAdminStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  updateCommunityMemberStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  getCommunityMembersStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  getCommunityGroupsStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  getBasicCommunityMembersStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  getBasicCommunityGroupStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  getRecentMembersStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  updateUserRolesStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  updateUserVerificationStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  createAdministratorStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  verifyUsersStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  deleteMembersStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  activateMembersStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  deactivateMembersStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  getCommunityGuestsStatus: {
    status: ReduxStatus;
    error: string | null;
  };
}

const initialState: MembersState = {
  members: [],
  memberPagination: {
    current: 0,
    pages: 0,
    total: 0,
  },
  groups: [],
  groupsPagination: {
    current: 0,
    pages: 0,
    total: 0,
  },
  guests: [],
  guestPagination: {
    current: 0,
    pages: 0,
    total: 0,
  },
  basicMembers: [],
  basicGroups: undefined,
  recentMembers: [],
  getRecentMembersStatus: {
    status: 'idle',
    error: null,
  },
  getCommunityMembersStatus: {
    status: 'idle',
    error: null,
  },
  createCommunityAdminStatus: {
    status: 'idle',
    error: null,
  },
  updateCommunityMemberStatus: {
    status: 'idle',
    error: null,
  },
  getCommunityGroupsStatus: {
    status: 'idle',
    error: null,
  },
  createCommunityGroupStatus: {
    status: 'idle',
    error: null,
  },
  getBasicCommunityMembersStatus: {
    status: 'idle',
    error: null,
  },
  getBasicCommunityGroupStatus: {
    status: 'idle',
    error: null,
  },
  updateUserRolesStatus: {
    status: 'idle',
    error: null,
  },
  updateUserVerificationStatus: {
    status: 'idle',
    error: null,
  },
  createAdministratorStatus: {
    status: 'idle',
    error: null,
  },
  deleteMembersStatus: {
    status: 'idle',
    error: null,
  },
  verifyUsersStatus: {
    status: 'idle',
    error: null,
  },
  activateMembersStatus: {
    status: 'idle',
    error: null,
  },
  deactivateMembersStatus: {
    status: 'idle',
    error: null,
  },
  getCommunityGuestsStatus: {
    status: 'idle',
    error: null,
  },
};

const membersSlice = createSlice({
  name: 'members',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getCommunityMembers.pending, (state) => {
      state.getCommunityMembersStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(getCommunityMembers.fulfilled, (state, { payload }) => {
      state.getCommunityMembersStatus = {
        status: 'success',
        error: null,
      };
      state.members = payload.model;
      state.memberPagination = payload.meta;
    });
    builder.addCase(getCommunityMembers.rejected, (state, { payload }) => {
      state.getCommunityMembersStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to fetch members',
      };
    });
    builder.addCase(createCommunityAdmin.pending, (state) => {
      state.createCommunityAdminStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(createCommunityAdmin.fulfilled, (state) => {
      state.createCommunityAdminStatus = {
        status: 'success',
        error: null,
      };
    });
    builder.addCase(createCommunityAdmin.rejected, (state, { payload }) => {
      state.createCommunityAdminStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to create community admin',
      };
    });
    builder.addCase(updateCommunityMember.pending, (state) => {
      state.updateCommunityMemberStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(updateCommunityMember.fulfilled, (state) => {
      state.updateCommunityMemberStatus = {
        status: 'success',
        error: null,
      };
    });
    builder.addCase(updateCommunityMember.rejected, (state, { payload }) => {
      state.updateCommunityMemberStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to update community member',
      };
    });
    builder.addCase(getBasicCommunityMembers.pending, (state) => {
      state.getBasicCommunityMembersStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(
      getBasicCommunityMembers.fulfilled,
      (state, { payload }) => {
        state.getBasicCommunityMembersStatus = {
          status: 'success',
          error: null,
        };
        state.basicMembers = payload;
      },
    );
    builder.addCase(getBasicCommunityMembers.rejected, (state, { payload }) => {
      state.getBasicCommunityMembersStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error tyring to fetch community members list',
      };
    });
    builder.addCase(getRecentMembers.pending, (state) => {
      state.getRecentMembersStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(getRecentMembers.fulfilled, (state, { payload }) => {
      state.getRecentMembersStatus = {
        status: 'success',
        error: null,
      };
      state.recentMembers = payload;
    });
    builder.addCase(getRecentMembers.rejected, (state, { payload }) => {
      state.getRecentMembersStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to fetch recent member list',
      };
    });
    builder.addCase(updateUserRole.pending, (state) => {
      state.updateUserRolesStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(updateUserRole.fulfilled, (state) => {
      state.updateUserRolesStatus = {
        status: 'success',
        error: null,
      };
    });
    builder.addCase(updateUserRole.rejected, (state, { payload }) => {
      state.updateUserRolesStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to update member role',
      };
    });
    builder.addCase(createAdministrator.pending, (state) => {
      state.createAdministratorStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(createAdministrator.fulfilled, (state) => {
      state.createAdministratorStatus = {
        status: 'success',
        error: null,
      };
    });
    builder.addCase(createAdministrator.rejected, (state, { payload }) => {
      state.createAdministratorStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to create administrator',
      };
    });
    builder.addCase(verifyUser.pending, (state) => {
      state.verifyUsersStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(verifyUser.fulfilled, (state) => {
      state.verifyUsersStatus = {
        status: 'success',
        error: null,
      };
    });
    builder.addCase(verifyUser.rejected, (state, { payload }) => {
      state.verifyUsersStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to send verifications',
      };
    });
    builder.addCase(deleteMembers.pending, (state) => {
      state.deleteMembersStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(deleteMembers.fulfilled, (state) => {
      state.deleteMembersStatus = {
        status: 'success',
        error: null,
      };
    });
    builder.addCase(deleteMembers.rejected, (state, { payload }) => {
      state.deleteMembersStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to delete members',
      };
    });
    builder.addCase(activateMembers.pending, (state) => {
      state.activateMembersStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(activateMembers.fulfilled, (state) => {
      state.activateMembersStatus = {
        status: 'success',
        error: null,
      };
    });
    builder.addCase(activateMembers.rejected, (state, { payload }) => {
      state.activateMembersStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to activate members',
      };
    });
    builder.addCase(deactivateMembers.pending, (state) => {
      state.deactivateMembersStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(deactivateMembers.fulfilled, (state) => {
      state.deactivateMembersStatus = {
        status: 'success',
        error: null,
      };
    });
    builder.addCase(deactivateMembers.rejected, (state, { payload }) => {
      state.deactivateMembersStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to deactivate members',
      };
    });
    builder.addCase(updateUserVerification.pending, (state) => {
      state.updateUserVerificationStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(updateUserVerification.fulfilled, (state) => {
      state.updateUserVerificationStatus = {
        status: 'success',
        error: null,
      };
    });
    builder.addCase(updateUserVerification.rejected, (state, { payload }) => {
      state.updateUserVerificationStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to verify member',
      };
    });
    builder.addCase(getCommunityGuests.pending, (state) => {
      state.getCommunityGuestsStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(getCommunityGuests.fulfilled, (state, { payload }) => {
      state.getCommunityGroupsStatus = {
        status: 'success',
        error: null,
      };
      state.guests = payload.model;
      state.guestPagination = payload.meta;
    });
    builder.addCase(getCommunityGuests.rejected, (state, { payload }) => {
      state.getCommunityGroupsStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to get unregistered users',
      };
    });
    builder.addCase(resetAll, () => initialState);
  },
});

export default membersSlice.reducer;
